import {useMemo} from 'react';
import {LogMessageType} from '../../services/LogMessagesType';
import {ApiResponsePending} from '../../services/ApiResponseType';
import {useApiService} from '../../services/helpers';
import {BatchAcknowledgementSaveDto} from '../../services/generated/models/BatchAcknowledgementSaveDto';
import {BatchAcknowledgementDto} from '../../services/generated/models/BatchAcknowledgementDto';
import { BatchAcknowledgementNotCompleted } from '../../models/BatchAcknowledgementNotCompleted';
import { AcknowledgementService } from '../../services/generated/services/AcknowledgementService';
import { Document } from '../../models';
import { AcknowledgementsProcess } from '../../models/AcknowledgementsProcess';
import { MyAcknowledgementModel } from '../../models/MyAcknowledgementModel';

export const useAcknowledgementBatch = () => {
  const requestWrapper = useApiService();

  const getAcknowledgementBatch = (
    batchId: number,
    log: LogMessageType = {
      error: true
    }
  ): ApiResponsePending<BatchAcknowledgementDto> => {
    return requestWrapper(
      AcknowledgementService.getApiBatchAcknowledgement(batchId),
      apiCode => apiCode,
      log
    );
  };


  const getUserWithDataToAcknowledge = (
    batchId: number,
    log: LogMessageType = {
      error: true
    }
  ): ApiResponsePending<BatchAcknowledgementNotCompleted[]> => {
    return requestWrapper(
      AcknowledgementService.getUserWithDataToAcknowledge(batchId),
      result => result.map(batch => new BatchAcknowledgementNotCompleted(batch)),
      log
    );
  };

  const getProcessesUsedInUnit = (
    unitId: number,
    log: LogMessageType = {
      error: true
    }
  ): ApiResponsePending<AcknowledgementsProcess[]> => {
    return requestWrapper(
      AcknowledgementService.getApiProcessesUsedInUnit(unitId),
      result => result.map(process => new AcknowledgementsProcess(process)),
      log
    );
  };

  const getDocumentsUsedInUnit = (
    unitId: number,
    log: LogMessageType = {
      error: true
    }
  ): ApiResponsePending<Document[]> => {
    return requestWrapper(
      AcknowledgementService.getApiDocumentsUsedInUnit(unitId),
      result => result.map(doc => new Document(doc)),
      log
    );
  };

  const getMyInitiatedAcknowledgement = (
    log: LogMessageType = {
      error: true
    }
  ): ApiResponsePending<MyAcknowledgementModel[]> => {
    return requestWrapper(
      AcknowledgementService.getApiMyInitiatedAcknowledgements(),
       result => result.map(item => new MyAcknowledgementModel(item)),
      log
    );
  };

  const getReceivedAcknowledgement = (
    log: LogMessageType = {
      error: true
    }
  ): ApiResponsePending<MyAcknowledgementModel[]> => {
    return requestWrapper(
      AcknowledgementService.getApiReceivedAcknowledgements(),
      result => result.map(item => new MyAcknowledgementModel(item)),
      log
    );
  };

  const postAcknowledgementForm = (
    requestBody: BatchAcknowledgementSaveDto,
    log: LogMessageType = {
      error: true,
      success: true,
      successMessage: `Acknowledgement form sent successfully`
    }
  ): ApiResponsePending<BatchAcknowledgementSaveDto[]> => {
    return requestWrapper(
      AcknowledgementService.postApiBatchAcknowledgement(requestBody),
      result => result,
      log
    );
  };

  const putAcknowledgementOpened = async (
    docId: number,
    log: LogMessageType = {
      error: true,
      success: true,
      successMessage: 'The document was marked as opened'
    }
  ): ApiResponsePending<string> => {
    return await requestWrapper(
      AcknowledgementService.putAcknowledgementOpened(docId),
      (result, apiCode) => apiCode,
      log
    );
  };

  const putAcknowledgementConfirmed = async (
    id: number,
    log: LogMessageType = {
      error: true,
      success: true,
      successMessage: 'The changes have been saved'
    }
  ): ApiResponsePending<string> => {
    return await requestWrapper(
      AcknowledgementService.putAcknowledgementConfirmed(id),
      (result, apiCode) => apiCode,
      log
    );
  };

  const putAcknowledgementFinished = async (
    batchId: number,
    log: LogMessageType = {
      error: true,
      success: true,
      successMessage: 'The Acknowledgement is finished'
    }
  ): ApiResponsePending<string> => {
    return await requestWrapper(
      AcknowledgementService.putAcknowledgementFinished(batchId),
      (result, apiCode) => apiCode,
      log
    );
  };

  return useMemo(
    () => ({
      getMyInitiatedAcknowledgement,
      getReceivedAcknowledgement,
      getAcknowledgementBatch,
      getProcessesUsedInUnit,
      getDocumentsUsedInUnit,
      getUserWithDataToAcknowledge,
      postAcknowledgementForm,
      putAcknowledgementOpened,
      putAcknowledgementConfirmed,
      putAcknowledgementFinished
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
};
